/**
 * View model for the Brand Updates 2026 beta home page.
 *
 * Design source: the Figma "Employee Experience | Home" components, rendered
 * through `components/dashboard-kit`, with the section contents settled by the
 * September 2026 Idea Lab synthesis ("Employee Home Page: Content Synthesis"):
 *
 *   - Hero: two to four tiles that answer "how much" or "how far", totals first,
 *     then one key number per active benefit. Any figure that comes from an
 *     outside source carries its date; projections say "estimate".
 *   - Needs Your Attention: one row format for every benefit (the fact, a date
 *     chip, what doing it unlocks, a verb). Hard dates first, then what was sent
 *     back, then unfinished setup. An empty list says so.
 *   - Active Benefits: one card per benefit in use, with progress, a dated status
 *     line and the last / this / next activity that answers "where is my money?".
 *   - More to Explore: only benefits the employer offers and the person has not
 *     started, ordered by their situation. Resources are worded as the question
 *     they answer and end with support.
 *   - New users get the lead benefit in the hero, three steps for how it works, one
 *     invitation to act, then the same Explore and Resources grids.
 *
 * This module is the ONLY place that knows how those sections map onto live TIO
 * data. It is deliberately a pure function of its inputs — no service injection,
 * no fetching — so the mapping is unit-testable and every data gap is visible in
 * one file. Figures the backend cannot answer yet are listed in
 * `app/components/beta/BETA_HOME_GAPS.md`.
 */
import { formatCurrency } from 'tio-ui/format';
import {
  PhArrowsClockwise,
  PhBank,
  PhBookOpen,
  PhCalendarCheck,
  PhChatsCircle,
  PhCoins,
  PhDeviceMobile,
  PhFileText,
  PhFirstAidKit,
  PhGlobe,
  PhGraduationCap,
  PhHandshake,
  PhHeadset,
  PhHeartbeat,
  PhLifebuoy,
  PhPencilSimple,
  PhReceipt,
  PhRocketLaunch,
  PhScales,
  PhSealQuestion,
  PhShieldCheck,
  PhSignature,
  PhTarget,
  PhUploadSimple,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import type {
  ContributionActionItem,
  ContributionPaymentRecord,
  DataSourceType,
  LiveTasApplication,
  SlrPageState,
} from 'tio-employee/services/reimagined-data';
import {
  EVIDENCE_ACTIONABLE_STATES,
  EVIDENCE_RETURNED_STATES,
  SUBMITTED_REVIEW_STATES,
} from 'tio-employee/utils/tas/attention-tasks';
import { sortByDeadline } from 'tio-employee/utils/dashboard-kit/attention';
import type { PhoneVerificationState } from 'tio-data/data/resources/phone-number.type';
import type { ReimaginedHomeModel } from 'tio-employee/routes/authenticated/reimagined/home';
import { MANAGE_ROUTE } from 'tio-employee/utils/tas/manage-link';
import type { TaskUrgency } from 'tio-employee/components/dashboard-kit/task-card';
import type { HeroActionKind } from 'tio-employee/components/dashboard-kit/hero';

/** A Phosphor glyph, sized by the kit through a `tio-icon-*` class. */
export type BetaIcon = ComponentLike<{ Element: SVGElement }>;

/** Which of the design's two user states to render. */
export type BetaHomeAudience = 'new' | 'active';

/** The benefits this page can describe; `account` covers rows about the person's own profile. */
export type BetaBenefitId =
  'tuition-assistance' | 'student-loan-repayment' | 'pslf' | 'syf' | 'wellness' | 'account';

/** One tile inset in the hero, rendered by the kit's `HeroStat` in the matching layout. */
export interface BetaHeroTile {
  id: string;
  kind: 'metric' | 'progress' | 'info';
  label: string;
  value?: string;
  /** Dated or qualified footnote: "as of Sep 12", "estimate", "eligible up to $5,250". */
  context?: string;
  progressValue?: number;
  progressMax?: number;
  /** Info tiles: the featured copy under the label. */
  body?: string;
  linkLabel?: string;
  linkRoute?: string;
  source: DataSourceType;
}

export interface BetaHeroAction {
  label: string;
  route: string;
  kind: HeroActionKind;
}

export interface BetaHero {
  title: string;
  /** New-user hero only: the one promise the page leads with. */
  lede?: string;
  action?: BetaHeroAction;
  tiles: BetaHeroTile[];
}

/**
 * How a row is ordered: a hard date, something sent back to the person,
 * unfinished setup, or a notice that asks nothing.
 */
export type BetaAttentionGroup = 'deadline' | 'returned' | 'setup' | 'info';

export interface BetaAttentionRow {
  id: string;
  benefit: BetaBenefitId;
  group: BetaAttentionGroup;
  urgency: TaskUrgency;
  /** The ISO date the row is ordered by (deadline and returned groups). */
  at?: string;
  /** The date chip, already formatted: "due Oct 14", "returned Sep 2". */
  dueLabel?: string;
  /** States the fact. */
  title: string;
  /** Says what doing it enables. */
  description: string;
  /** A verb. */
  cta: string;
  route?: string;
  /** Dynamic segment for `route`, when it takes one. */
  model?: string;
  icon: BetaIcon;
}

export interface BetaAttention {
  rows: BetaAttentionRow[];
  emptyTitle: string;
  emptyBody: string;
}

/** A row of the last / this / next list (or recent requests) on a benefit card. */
export interface BetaActivityRow {
  label: string;
  value: string;
  note?: string;
}

export interface BetaBenefitCard {
  id: Exclude<BetaBenefitId, 'account'>;
  title: string;
  icon: BetaIcon;
  route: string;
  linkLabel: string;
  headline: {
    value: string;
    label: string;
    progress?: { value: number; max: number };
  };
  /** Dated status lines for anything pending, including things waiting on someone else. */
  statusLines: string[];
  activity: BetaActivityRow[];
  footerLink?: { label: string; route: string };
  source: DataSourceType;
}

export interface BetaDiscoverCard {
  id: string;
  title: string;
  description: string;
  /** "More to Explore" call-to-action line; absent on Resources cards. */
  incentive?: string;
  icon: BetaIcon;
  route: string;
}

/** One card of the new-user "How [Benefit] Works" row. */
export interface BetaHowStep {
  id: string;
  title: string;
  description: string;
  icon: BetaIcon;
}

/** The new-user section for the lead benefit: three steps and one invitation to act. */
export interface BetaHowItWorks {
  title: string;
  steps: BetaHowStep[];
  banner: { prompt: string; label: string; route: string };
}

/** A Recommendation card item: what to ask. The home component supplies how. */
export interface BetaRecommendation {
  id: string;
  icon: BetaIcon;
  title: string;
  description: string;
}

export interface BetaHomeModel {
  audience: BetaHomeAudience;
  greeting: string;
  firstName: string;
  hero: BetaHero;
  attention: BetaAttention;
  /** New users only: how the lead benefit works, and the one action to take; null when no benefit is offered. */
  howItWorks: BetaHowItWorks | null;
  /** Established members only: the first-run page keeps to the welcome and How it works. */
  recommendations: BetaRecommendation[];
  benefitCards: BetaBenefitCard[];
  /** "Explore Your Benefits" (new) / "More to Explore" (active). */
  discover: BetaDiscoverCard[];
  resources: BetaDiscoverCard[];
}

/** Partner/plan switches that gate which cards appear. */
export interface BetaHomeFlags {
  showTuitionAssistance: boolean;
  showStudentLoanRepayment: boolean;
  showPslf: boolean;
  /** Its own entitlement (`partner.hasWellness`), not a loan sub-benefit. */
  showFinancialWellness: boolean;
  showLpn: boolean;
  showMentoring: boolean;
  showSyf: boolean;
  showIdr: boolean;
  showPhoneCapture: boolean;
}

/**
 * Every benefit on. The default for `audienceFor` so its existing callers keep
 * their behaviour; `buildBetaHomeModel` always passes the real flags.
 */
const ALL_BENEFITS_ON: BetaHomeFlags = {
  showPhoneCapture: true,
  showTuitionAssistance: true,
  showStudentLoanRepayment: true,
  showPslf: true,
  showFinancialWellness: true,
  showLpn: true,
  showMentoring: true,
  showSyf: true,
  showIdr: true,
};

/** Loan-repayment activity read off the contribution history. */
export interface BetaContributionActivity {
  /** The newest contribution that reached the servicer. */
  lastPayment: ContributionPaymentRecord | null;
  /** The newest contribution, when the servicer sent it back and nothing has gone out since. */
  returnedPayment: ContributionPaymentRecord | null;
  /** The contribution being prepared, from the preview endpoint. */
  nextPayment: ContributionPaymentRecord | null;
  /** The plan's configured per-cycle amount in dollars. */
  scheduledMonthlyAmount: number | null;
  pageState?: SlrPageState;
  accountReceivingName: string | null;
}

/** The loan-repayment plan on the session, in dollars. */
export interface BetaPlanSignals {
  annualLimit: number | null;
  cadence: string | null;
}

/** Secure Your Future enrollment, read off the session's match participant. */
export interface BetaSyfSignals {
  enrollment: 'none' | 'enrolling' | 'enrolled';
  /** The current plan year's final certification date, ISO. */
  certificationDeadline: string | null;
  benefitYear: string | null;
  enrolledAt: string | null;
}

export type BetaWellnessScore = 'on-track' | 'needs-adjustment' | 'needs-attention';

/** The Financial Wellness train, read off the authenticated route's raw payload. */
export interface BetaWellnessSignals {
  score: BetaWellnessScore | null;
  /** When the train was last calculated, ISO. */
  asOf: string | null;
  /** The newest statement or loan file date behind the balances, ISO. */
  balanceAsOf: string | null;
  /** The NSLDS file's own as-of date, ISO. */
  nsldsAsOf: string | null;
  hasLinkedLoans: boolean;
}

export interface BetaHomeInput {
  model: ReimaginedHomeModel;
  firstName: string;
  /** Hour of day, injected so the greeting is testable. */
  hour: number;
  /** The current time in epoch milliseconds, injected so every date is testable. */
  now: number;
  /**
   * Server-driven loan-repayment action items, from
   * `reimaginedData.fetchContributionHistory().actionItems`. These already carry
   * their own badge copy and a danger/warning/default intent.
   */
  actionItems: ContributionActionItem[];
  flags: BetaHomeFlags;
  /**
   * The mobile-number recommendation's inputs. Copy is resolved by the caller: this builder
   * is a pure function with no container, and its unit tests rely on that.
   */
  phoneCapture?: {
    state: PhoneVerificationState;
    title: string;
    description: string;
    /** The server's card eligibility, false once either prompt is snoozed this session. */
    eligible: boolean;
  };
  /**
   * Tuition assistance spent in the current benefit year.
   *
   * GAP (ta-ytd): `LiveTasData.summary` exposes lifetime `totalPaid` but no
   * benefit-year split, so the caller supplies this from
   * `reimaginedData.fetchTasUsedAmount()`. Pass `null` when it could not be
   * resolved — the figure then renders em-dash rather than a wrong zero.
   */
  tuitionYearToDate: number | null;
  /** Loan-repayment activity; null when the history could not be loaded. */
  contributionActivity?: BetaContributionActivity | null;
  plan?: BetaPlanSignals | null;
  syf?: BetaSyfSignals | null;
  wellness?: BetaWellnessSignals | null;
  /** The employer's display name, for copy such as "Awaiting Acme's signature". */
  employerName?: string | null;
}

const PSLF_REQUIRED_PAYMENTS = 120;

/** At most this many hero tiles; totals take the first slot when there are any. */
export const HERO_TILE_CAP = 4;

/** Two rows of the explore grid; relevance order decides what makes the cut. */
export const DISCOVER_CARD_CAP = 8;

/**
 * Three months, the threshold the product-agreed copy deck applies to statements,
 * NSLDS files and strategy runs alike. (Loan coaching suggested six months for
 * loan files; one threshold keeps every "from {date}" row consistent.)
 */
export const STALE_AFTER_DAYS = 92;

/** A dated row inside this many days reads as urgent; inside `SOON_WITHIN_DAYS` as soon. */
export const URGENT_WITHIN_DAYS = 7;
export const SOON_WITHIN_DAYS = 30;

/** The last-year window for a certification deadline row: inside it the row shows. */
const SYF_CERTIFY_WINDOW_DAYS = 60;

const DAY_MS = 86_400_000;
const ONE_YEAR_DAYS = 365;

const COACHING_ROUTE = 'authenticated.repaying-student-debt.coaching';
const STRATEGY_FINDER_ROUTE = 'authenticated.repaying-student-debt.repayment-strategy-finder';
const LOAN_SUMMARY_ROUTE = 'authenticated.account-activity.accounts';
const PSLF_ROUTE = 'authenticated.pslf.dashboard';
const SLR_ROUTE = 'authenticated.slr.dashboard';
const TAS_ROUTE = 'authenticated.tas.dashboard';
const SYF_ROUTE = 'authenticated.syf.dashboard';
const SYF_ENROLLMENT_ROUTE = 'authenticated.syf.enrollment';
const WELLNESS_ROUTE = 'authenticated.wellness-insights';
const CONTACT_ROUTE = 'authenticated.contact-us';
const KNOWLEDGE_BASE_ROUTE = 'authenticated.questions-you-have.glossary';
const REPAYMENT_PLANS_ROUTE = 'authenticated.questions-you-have.repayment-plans';
/** The rail's own FAQ destination: there is no FAQ route, so the two point at the same page. */
const FAQS_ROUTE = 'authenticated.questions-you-have.paying-for-college';

export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * TAS states where the employee owes an action, taken from the canonical set in
 * `utils/tas/attention-tasks.ts` rather than re-listed here. That module derives
 * the exact per-task list for ONE application's manage screen; this is a
 * dashboard roll-up that links INTO the manage screen.
 *
 * `EVIDENCE_ACTIONABLE_STATES` deliberately excludes PENDING_COURSES_APPROVAL
 * and PENDING_EVIDENCE_APPROVAL — those wait on an approver, so surfacing them
 * would tell people to act when there is nothing they can do.
 */
const TAS_EVIDENCE_DUE_STATES = new Set(
  [...EVIDENCE_ACTIONABLE_STATES].filter((state) => !EVIDENCE_RETURNED_STATES.has(state))
);

const TAS_COURSES_RETURNED_STATES = new Set([
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
]);

const TAS_DRAFT_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

const TAS_PAYMENT_SCHEDULED_STATES = new Set([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.PENDING_PAYMENT',
]);

const TAS_PAID_STATE = 'TAS.ApplicationState.FULFILLED';

/** PSLF form statuses, from `tio-data`'s pslf-form constants. */
const PSLF_SUBMITTED = new Set(['submitted', 're_submitted']);
const PSLF_IN_TRANSIT = new Set([
  'employer_signed',
  'awaiting_submit',
  'submit_pending',
  're_submit_pending',
]);
const PSLF_DEAD = new Set(['cancelled', 'expired', 'abandoned']);

/** The subset of a PSLF form this page reads; the live record is a `PslfFormModel`. */
interface PslfFormLike {
  status?: string;
  createdAt?: string | null;
  submittedAt?: string | null;
  borrowerCompletedAt?: string | null;
  employerDisplayName?: string | null;
  isExpiring?: boolean;
}

/** Whole dollars — the design shows no cents anywhere. */
function money(amount: number): string {
  return formatCurrency(amount, 0);
}

/**
 * Parses an ISO date (`2026-10-14`), month (`2026-10`) or datetime into local
 * epoch milliseconds. A date-only string is read as a LOCAL date: `new Date`
 * would take it as UTC midnight and print the day before in US time zones.
 */
export function parseDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const parts = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(value);
  if (parts) {
    const time = new Date(
      Number(parts[1]),
      Number(parts[2]) - 1,
      parts[3] ? Number(parts[3]) : 1
    ).getTime();
    return Number.isNaN(time) ? null : time;
  }
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

/** "Oct 14", with the year added when it is not the current one. */
export function formatDate(value: string | null | undefined, now: number): string {
  const time = parseDate(value);
  if (time === null) return '';
  const date = new Date(time);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** "May", with the year added when it is not the current one. */
export function formatMonth(value: string | null | undefined, now: number): string {
  const time = parseDate(value);
  if (time === null) return '';
  const date = new Date(time);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

/** Whole days from `now` to the date; negative once it has passed. */
export function daysUntil(value: string | null | undefined, now: number): number | null {
  const time = parseDate(value);
  return time === null ? null : Math.ceil((time - now) / DAY_MS);
}

/** Older than the three-month staleness threshold. */
export function isStale(value: string | null | undefined, now: number): boolean {
  const days = daysUntil(value, now);
  return days !== null && -days > STALE_AFTER_DAYS;
}

/** The kit leaves day thresholds to the caller; these are the page's. */
export function urgencyForDeadline(days: number | null): TaskUrgency {
  if (days === null) return 'onTrack';
  if (days <= URGENT_WITHIN_DAYS) return 'urgent';
  if (days <= SOON_WITHIN_DAYS) return 'soon';
  return 'onTrack';
}

function addDays(value: string, days: number): string {
  const time = parseDate(value);
  if (time === null) return value;
  return new Date(time + days * DAY_MS).toISOString();
}

function plural(count: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${count} ${count === 1 ? noun : pluralNoun}`;
}

/**
 * Highest qualifying-payment count across the person's loans.
 *
 * Mirrors the shipped beta dashboard's derivation rather than inventing a
 * second one: PSLF forgiveness tracks the furthest-along loan.
 */
export function pslfQualifyingPayments(model: ReimaginedHomeModel): number {
  const counts = model.pslfData.accounts.flatMap((account) =>
    account.loans.map((loan) => loan.qualifyingPayments ?? 0)
  );
  return counts.length ? Math.max(...counts) : 0;
}

/** The newest NSLDS observation date across the person's PSLF loans, ISO. */
export function pslfDataAsOf(model: ReimaginedHomeModel): string | null {
  let latest: string | null = null;
  let latestTime = -Infinity;
  for (const account of model.pslfData.accounts) {
    for (const loan of account.loans) {
      const time = parseDate(loan.updatedAt);
      if (time !== null && time > latestTime) {
        latestTime = time;
        latest = loan.updatedAt ?? null;
      }
    }
  }
  return latest;
}

/** Signals outside the snapshot bundle that also mean "this person has transacted". */
export interface AudienceSignals {
  /** Enrollment started or complete: a brand-new account has neither. */
  syfStarted?: boolean;
  hasLinkedLoans?: boolean;
}

/**
 * Whether the person has any recorded benefit activity.
 *
 * Drives the design's New/Active split. Deliberately generous: any employer
 * money received, any TA program or application, any PSLF form on file, a started
 * SYF enrollment, or linked loans at a loan-benefit employer counts as active,
 * because the "Welcome" hero reads wrong to someone who has already transacted.
 */
export function audienceFor(
  model: ReimaginedHomeModel,
  flags: BetaHomeFlags = ALL_BENEFITS_ON,
  signals: AudienceSignals = {}
): BetaHomeAudience {
  const { employerContributions, tasData, pslfData } = model;

  // Only activity in a benefit the partner OFFERS can make someone an active
  // user. Otherwise leftover data in a disabled benefit — a ta-only partner
  // whose contribution history predates the plan change — renders the
  // active-user hero for a benefit whose cards and nav entry are all hidden.
  const hasEmployerMoney =
    flags.showStudentLoanRepayment && employerContributions.contributions.lifetime > 0;
  const hasTas =
    flags.showTuitionAssistance &&
    (tasData.programs.length > 0 ||
      tasData.applications.length > 0 ||
      tasData.summary.activeCount > 0);
  const hasPslf = flags.showPslf && (pslfData.forms.length > 0 || pslfData.accounts.length > 0);
  const hasSyf = flags.showSyf && Boolean(signals.syfStarted);
  const hasLoans =
    (flags.showPslf || flags.showStudentLoanRepayment || flags.showFinancialWellness) &&
    Boolean(signals.hasLinkedLoans);

  return hasEmployerMoney || hasTas || hasPslf || hasSyf || hasLoans ? 'active' : 'new';
}

/* ───────────────────────── per-benefit readings ───────────────────────── */

/** Everything the page says about Tuition Assistance, derived once. */
interface TasReading {
  applications: LiveTasApplication[];
  /** A program, a request or an active count on file: the benefit is in use. */
  inUse: boolean;
  /** Approved this year; null when the figure could not be resolved. */
  used: number | null;
  limit: number;
  inReview: number;
  waitingOnYou: number;
  paymentScheduled: number;
  paid: number;
  courseCashReserved: number;
  courseCashUsed: number;
  rows: BetaAttentionRow[];
  source: DataSourceType;
}

function readTas(input: BetaHomeInput): TasReading {
  const { model, now } = input;
  // An instance row's id is a program-instance id; MANAGE_ROUTE resolves a
  // tas-application, so every row below would 404 on one.
  const applications = model.tasData.applications.filter(
    (application) => application.recordType === 'application'
  );
  const rows: BetaAttentionRow[] = [];
  let inReview = 0;
  let waitingOnYou = 0;
  let paymentScheduled = 0;
  let paid = 0;
  let courseCashReserved = 0;
  let courseCashUsed = 0;

  for (const application of applications) {
    const label = application.programName ?? application.name;
    const touched = application.updatedAt ?? application.createdAt;
    courseCashReserved += application.courseCashReserved ?? 0;
    courseCashUsed += application.courseCashUsed ?? 0;

    if (SUBMITTED_REVIEW_STATES.has(application.state)) inReview += 1;
    if (TAS_PAYMENT_SCHEDULED_STATES.has(application.state)) paymentScheduled += 1;
    if (application.state === TAS_PAID_STATE) paid += 1;

    const common = {
      benefit: 'tuition-assistance' as const,
      route: MANAGE_ROUTE,
      model: application.id,
      icon: PhGraduationCap,
    };

    if (EVIDENCE_RETURNED_STATES.has(application.state)) {
      waitingOnYou += 1;
      rows.push({
        ...common,
        id: `tas-evidence-returned-${application.id}`,
        group: 'returned',
        urgency: 'urgent',
        at: touched,
        dueLabel: touched ? `returned ${formatDate(touched, now)}` : undefined,
        title: 'Your evidence was returned with feedback',
        description: `Your approver left notes on ${label}. Reviewing them and resubmitting gets the request moving again.`,
        cta: 'Review feedback',
      });
    } else if (TAS_COURSES_RETURNED_STATES.has(application.state)) {
      waitingOnYou += 1;
      rows.push({
        ...common,
        id: `tas-courses-returned-${application.id}`,
        group: 'returned',
        urgency: 'urgent',
        at: touched,
        dueLabel: touched ? `returned ${formatDate(touched, now)}` : undefined,
        title: 'Your course request was returned with feedback',
        description: `Your approver left notes on ${label}. Updating the course details sends it back for approval.`,
        cta: 'Update request',
      });
    } else if (TAS_EVIDENCE_DUE_STATES.has(application.state)) {
      waitingOnYou += 1;
      // GAP (ta-evidence-due): the evidence deadline is computed server-side for
      // the auto-rejection job and is not on the payload, so the dated fact is
      // when the courses end — the point from which the clock runs.
      const ends = application.coursesEndDate;
      const days = daysUntil(ends, now);
      const ended = days !== null && days <= 0;
      rows.push({
        ...common,
        id: `tas-evidence-due-${application.id}`,
        group: ends ? 'deadline' : 'setup',
        urgency: ended ? 'soon' : 'onTrack',
        at: ends ?? undefined,
        dueLabel: ends
          ? `${ended ? 'courses ended' : 'courses end'} ${formatDate(ends, now)}`
          : undefined,
        title: `Evidence is due for ${label}`,
        description:
          'Submitting your grades and receipts once your courses end keeps this request on track for payment.',
        cta: 'Submit evidence',
      });
    } else if (TAS_DRAFT_STATES.has(application.state)) {
      waitingOnYou += 1;
      rows.push({
        ...common,
        id: `tas-draft-${application.id}`,
        group: 'setup',
        urgency: 'onTrack',
        at: touched,
        dueLabel: touched ? `last edited ${formatDate(touched, now)}` : undefined,
        title: `Your ${label} request is saved as a draft`,
        description:
          'Pick up where you left off. Once submitted it goes to your approver for review.',
        cta: 'Finish request',
        icon: PhPencilSimple,
      });
    } else if (application.inRepayment) {
      // Last, not first: money owed outlives the state machine, but where an
      // application is also actionable the row the employee can act on wins.
      rows.push({
        ...common,
        id: `tas-in-repayment-${application.id}`,
        group: 'returned',
        urgency: 'urgent',
        at: touched,
        dueLabel: touched ? `since ${formatDate(touched, now)}` : undefined,
        title: `A balance is due on ${label}`,
        description:
          'This request entered repayment. The details show what is owed and how to settle it.',
        cta: 'Review balance',
        icon: PhScales,
      });
    }
  }

  return {
    applications,
    inUse:
      model.tasData.programs.length > 0 ||
      model.tasData.applications.length > 0 ||
      model.tasData.summary.activeCount > 0,
    // Never `section127Info.yearToDateUsed`: that total adds loan contributions.
    used: input.tuitionYearToDate,
    limit: model.section127Info.annualLimit,
    inReview,
    waitingOnYou,
    paymentScheduled,
    paid,
    courseCashReserved,
    courseCashUsed,
    rows,
    source: model.tasData.isLiveData ? 'live' : 'demo',
  };
}

/** "eligible up to $X this year" until the limit is met; approvals can meet or pass a cap, and "up to" would then contradict the figure. */
function tasLimitContext(used: number, limit: number): string {
  if (limit <= 0) return 'approved requests';
  return used >= limit ? 'annual limit reached' : `eligible up to ${money(limit)} this year`;
}

/** The status a request shows on the Active Benefits card's recent list. */
function tasStatusLabel(application: LiveTasApplication): string {
  const { state } = application;
  if (state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') return 'In review';
  if (state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') return 'Evidence in review';
  if (EVIDENCE_RETURNED_STATES.has(state)) return 'Evidence returned';
  if (TAS_COURSES_RETURNED_STATES.has(state)) return 'Request returned';
  if (TAS_EVIDENCE_DUE_STATES.has(state)) return 'Evidence due';
  if (TAS_PAYMENT_SCHEDULED_STATES.has(state)) return 'Approved · payment scheduled';
  if (state === TAS_PAID_STATE) return 'Paid';
  if (TAS_DRAFT_STATES.has(state)) return 'Draft';
  if (state === 'TAS.ApplicationState.ABANDONED') return 'Abandoned';
  if (state === 'TAS.ApplicationState.REJECTED_PER_POLICY') return 'Not eligible';
  return 'In progress';
}

/** The deck's copy for the server-authored loan-repayment alerts, keyed by alert type. */
function slrAlertCopy(
  item: ContributionActionItem,
  servicer: string
): { title: string; description: string; cta: string } {
  switch (item.id) {
    case 'slr-blank-account-number':
      return {
        title: `Add your account number for ${servicer}`,
        description: `Payments to ${servicer} need an account number to be applied to your loan. Adding it lets the next contribution go out.`,
        cta: 'Add account number',
      };
    case 'slr-blank-payment-address':
      return {
        title: `Add a payment address for ${servicer}`,
        description: `Contributions to ${servicer} are sent by check. A payment address lets the next contribution go out.`,
        cta: 'Add address',
      };
    case 'slr-no-loan-balance':
      return {
        title: `Upload a recent statement for ${servicer}`,
        description:
          'A current balance is what sizes your contribution. A recent statement provides it.',
        cta: 'Upload statement',
      };
    case 'slr-invalid-loan-type':
      return {
        title: `Your ${servicer} account is not an eligible loan type`,
        description:
          'Contributions apply to eligible student loans. If this account is labelled incorrectly, a statement lets us re-check it.',
        cta: 'Upload statement',
      };
    case 'slr-grouped-account-info':
      return {
        title: `Add your ${servicer} account details`,
        description: item.description,
        cta: 'Add account details',
      };
    default:
      // The API authors this copy; keep it for alert types the deck has not covered.
      return { title: item.title, description: item.description, cta: 'View details' };
  }
}

function slrAlertUrgency(item: ContributionActionItem): TaskUrgency {
  if (item.badgeIntent === 'danger') return 'urgent';
  if (item.badgeIntent === 'warning') return 'soon';
  return 'info';
}

interface SlrReading {
  /** Money has moved or is scheduled: the benefit is in use. */
  inUse: boolean;
  yearToDate: number;
  lifetime: number;
  annualLimit: number;
  activity: BetaContributionActivity | null;
  rows: BetaAttentionRow[];
  source: DataSourceType;
}

/**
 * A contribution still to come. A preview cycle earlier than the current month
 * is a stale projection, not a next payment, so it is dropped rather than shown
 * as "next".
 */
function upcomingPayment(
  activity: BetaContributionActivity | null,
  now: number
): ContributionPaymentRecord | null {
  const next = activity?.nextPayment;
  if (!next) return null;
  const cycle = parseDate(next.paidDate ?? next.benefitCycle);
  const today = new Date(now);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).getTime();
  return cycle !== null && cycle < monthStart ? null : next;
}

function readSlr(input: BetaHomeInput): SlrReading {
  const { model, now } = input;
  const { contributions, isLiveData } = model.employerContributions;
  const given = input.contributionActivity ?? null;
  const activity = given ? { ...given, nextPayment: upcomingPayment(given, now) } : null;
  const servicer = activity?.accountReceivingName ?? 'your servicer';
  const nextMonth = activity?.nextPayment
    ? formatMonth(activity.nextPayment.paidDate ?? activity.nextPayment.benefitCycle, now)
    : '';
  const rows: BetaAttentionRow[] = [];

  for (const item of input.actionItems) {
    // The deck puts "paid in full" on the card as a status line, never as a task.
    if (item.id === 'slr-all-pif') continue;
    const copy = slrAlertCopy(item, servicer);
    rows.push({
      id: item.id.startsWith('slr-') ? item.id : `slr-${item.id}`,
      benefit: 'student-loan-repayment',
      group: item.id === 'slr-alerts-unavailable' ? 'info' : 'setup',
      urgency: slrAlertUrgency(item),
      dueLabel:
        nextMonth && item.blocksContribution !== false
          ? `next contribution ${nextMonth}`
          : undefined,
      ...copy,
      /**
       * Deliberately NOT `item.route`. Those routes were authored for the alpha
       * surface — `authenticated.reimagined.loans` for dropdown resolutions, and
       * `authenticated.observability.upload` for uploads, which needs `source` /
       * `providers` query params this roll-up has no way to supply. The real SLR
       * dashboard renders these same action items with their resolution
       * controls attached, so it resolves every variant without parameters.
       */
      route: SLR_ROUTE,
      icon: item.resolutionType === 'upload' ? PhUploadSimple : PhHandshake,
    });
  }

  // GAP (slr-returned-payment): no alert type covers a payment the servicer sent
  // back, so the row is derived from the history itself: the newest contribution
  // failed and nothing has gone out since.
  const returned = activity?.returnedPayment;
  if (returned) {
    const date = returned.paidDate ?? returned.benefitCycle;
    rows.push({
      id: `slr-returned-payment-${returned.id}`,
      benefit: 'student-loan-repayment',
      group: 'returned',
      urgency: 'urgent',
      at: date,
      dueLabel: `returned ${formatDate(date, now)}`,
      title: `A ${money(returned.amount)} contribution to ${returned.servicer} was returned`,
      description: `${returned.servicer} sent the ${formatMonth(returned.benefitCycle, now)} payment back. Checking the account details lets it be resent.`,
      cta: 'Review payment',
      route: SLR_ROUTE,
      icon: PhArrowsClockwise,
    });
  }

  return {
    inUse: contributions.lifetime > 0 || Boolean(activity?.nextPayment),
    yearToDate: contributions.yearToDate,
    lifetime: contributions.lifetime,
    annualLimit: input.plan?.annualLimit ?? contributions.projectedAnnual,
    activity,
    rows,
    source: isLiveData ? 'live' : 'demo',
  };
}

interface PslfReading {
  payments: number;
  asOf: string | null;
  forms: PslfFormLike[];
  latestForm: PslfFormLike | null;
  submittedCount: number;
  lastSubmittedAt: string | null;
  /** A form or loan data on file: the benefit is in use. */
  inUse: boolean;
  rows: BetaAttentionRow[];
  source: DataSourceType;
}

function newestForm(forms: PslfFormLike[]): PslfFormLike | null {
  let newest: PslfFormLike | null = null;
  let newestTime = -Infinity;
  for (const form of forms) {
    if (form.status && PSLF_DEAD.has(form.status)) continue;
    const time = parseDate(form.createdAt) ?? 0;
    if (time >= newestTime) {
      newestTime = time;
      newest = form;
    }
  }
  return newest;
}

function readPslf(input: BetaHomeInput): PslfReading {
  const { model, now } = input;
  const forms = model.pslfData.forms as PslfFormLike[];
  const payments = pslfQualifyingPayments(model);
  const asOf = pslfDataAsOf(model) ?? input.wellness?.nsldsAsOf ?? null;
  const hasLoanData = model.pslfData.accounts.length > 0;
  const rows: BetaAttentionRow[] = [];
  const employer = input.employerName ?? 'your employer';

  const submitted = forms.filter((form) => form.status && PSLF_SUBMITTED.has(form.status));
  const lastSubmittedAt = submitted
    .map((form) => form.submittedAt ?? null)
    .filter((date): date is string => Boolean(date))
    .sort((a, b) => (parseDate(b) ?? 0) - (parseDate(a) ?? 0))[0];

  for (const form of forms) {
    if (form.status === 'created') {
      rows.push({
        id: 'pslf-sign-form',
        benefit: 'pslf',
        group: 'setup',
        urgency: 'soon',
        at: form.createdAt ?? undefined,
        dueLabel: form.createdAt ? `started ${formatDate(form.createdAt, now)}` : undefined,
        title: 'Your PSLF form is ready for your signature',
        description: `Signing sends it to ${employer} for their part. You will be notified when it is complete.`,
        cta: 'Sign form',
        route: PSLF_ROUTE,
        icon: PhSignature,
      });
      break;
    }
  }

  /**
   * Only when a form is actually near recertification. `isExpiring` is the model's
   * own definition of that — submitted or pending, and old enough to be inside the
   * recert warning window. The due date is a year after the last submission.
   */
  const expiring = forms.find((form) => form.isExpiring);
  if (expiring) {
    const reference = expiring.submittedAt ?? expiring.createdAt ?? null;
    const due = reference ? addDays(reference, ONE_YEAR_DAYS) : null;
    const days = daysUntil(due, now);
    rows.push({
      id: 'pslf-certification',
      benefit: 'pslf',
      group: due ? 'deadline' : 'setup',
      urgency: urgencyForDeadline(days),
      at: due ?? undefined,
      dueLabel: due ? `due ${formatDate(due, now)}` : undefined,
      title: due
        ? `Your annual PSLF certification is due ${formatDate(due, now)}`
        : 'Your annual PSLF certification is due',
      description: `${reference ? `Your last form was submitted ${formatDate(reference, now)}. ` : ''}Certifying each year records another year of qualifying employment.`,
      cta: 'Start certification',
      route: PSLF_ROUTE,
      icon: PhFileText,
    });
  }

  if (forms.length > 0 && !hasLoanData) {
    rows.push({
      id: 'pslf-add-loan-data',
      benefit: 'pslf',
      group: 'setup',
      urgency: 'onTrack',
      title: 'Add your loan data to track qualifying payments',
      description: 'Uploading your NSLDS file shows qualifying payments across all your loans.',
      cta: 'Upload loan data',
      route: PSLF_ROUTE,
      icon: PhUploadSimple,
    });
  } else if (hasLoanData && asOf && isStale(asOf, now)) {
    rows.push({
      id: 'pslf-stale-loan-data',
      benefit: 'pslf',
      group: 'setup',
      urgency: 'onTrack',
      at: asOf,
      dueLabel: `as of ${formatDate(asOf, now)}`,
      title: `Your loan data is from ${formatDate(asOf, now)}`,
      description: 'A fresh NSLDS file updates your qualifying payment count.',
      cta: 'Update loan data',
      route: PSLF_ROUTE,
      icon: PhArrowsClockwise,
    });
  }

  return {
    payments,
    asOf,
    forms,
    latestForm: newestForm(forms),
    submittedCount: submitted.length,
    lastSubmittedAt: lastSubmittedAt ?? null,
    inUse: forms.length > 0 || hasLoanData,
    rows,
    source: model.pslfData.isLiveData ? 'live' : 'demo',
  };
}

interface SyfReading {
  signals: BetaSyfSignals | null;
  /** Enrollment started or complete: the benefit is in use. */
  inUse: boolean;
  rows: BetaAttentionRow[];
}

function readSyf(input: BetaHomeInput): SyfReading {
  const signals = input.syf ?? null;
  const rows: BetaAttentionRow[] = [];
  const employer = input.employerName ?? 'your employer';
  const inUse = signals !== null && signals.enrollment !== 'none';
  if (!signals) return { signals, inUse, rows };

  if (signals.enrollment === 'enrolling') {
    rows.push({
      id: 'syf-finish-enrollment',
      benefit: 'syf',
      group: 'setup',
      urgency: 'soon',
      title: 'Finish enrolling in Secure Your Future',
      description:
        'Once complete, your qualifying loan payments start counting toward your 401(k) match.',
      cta: 'Finish enrollment',
      route: SYF_ENROLLMENT_ROUTE,
      icon: PhRocketLaunch,
    });
  }

  // GAP (syf-summary): no per-employee summary carries the uncertified count, so
  // the deadline row shows inside the certification window without a count.
  const days = daysUntil(signals.certificationDeadline, input.now);
  if (signals.enrollment === 'enrolled' && days !== null && days <= SYF_CERTIFY_WINDOW_DAYS) {
    rows.push({
      id: 'syf-certify',
      benefit: 'syf',
      group: 'deadline',
      urgency: urgencyForDeadline(days),
      at: signals.certificationDeadline ?? undefined,
      dueLabel: `due ${formatDate(signals.certificationDeadline, input.now)}`,
      title: `Certify your qualifying payments by ${formatDate(signals.certificationDeadline, input.now)}`,
      description: `Certifying is what sends your verified loan payments to ${employer} for the match.`,
      cta: 'Certify payments',
      route: SYF_ROUTE,
      icon: PhCalendarCheck,
    });
  }

  return { signals, inUse, rows };
}

interface WellnessReading {
  signals: BetaWellnessSignals | null;
  /** Linked loans or a calculated train: the benefit is in use. */
  inUse: boolean;
  rows: BetaAttentionRow[];
}

const WELLNESS_SCORE_LABEL: Record<BetaWellnessScore, string> = {
  'on-track': 'On track',
  'needs-adjustment': 'Needs adjustment',
  'needs-attention': 'Needs attention',
};

function readWellness(input: BetaHomeInput): WellnessReading {
  const signals = input.wellness ?? null;
  const rows: BetaAttentionRow[] = [];
  const inUse = Boolean(signals && (signals.hasLinkedLoans || signals.score));

  if (signals?.hasLinkedLoans && signals.balanceAsOf && isStale(signals.balanceAsOf, input.now)) {
    const date = formatDate(signals.balanceAsOf, input.now);
    rows.push({
      id: 'wellness-stale-balance',
      benefit: 'wellness',
      group: 'setup',
      urgency: 'onTrack',
      at: signals.balanceAsOf,
      dueLabel: `statement dated ${date}`,
      title: `Your loan balance is from ${date}`,
      description:
        'A statement from the last three months brings your balance, rate and payment history up to date.',
      cta: 'Upload statement',
      route: LOAN_SUMMARY_ROUTE,
      icon: PhUploadSimple,
    });
  }

  return { signals, inUse, rows };
}

/* ───────────────────────────── the sections ───────────────────────────── */

interface Readings {
  tas: TasReading;
  slr: SlrReading;
  pslf: PslfReading;
  syf: SyfReading;
  wellness: WellnessReading;
}

/** Soonest dated row for a benefit, in days; Infinity without one. Picks hero tiles. */
function soonestDeadline(rows: BetaAttentionRow[], now: number): number {
  let soonest = Infinity;
  for (const row of rows) {
    if (row.group !== 'deadline') continue;
    const days = daysUntil(row.at, now);
    if (days !== null && days < soonest) soonest = days;
  }
  return soonest;
}

function buildHero(input: BetaHomeInput, readings: Readings, audience: BetaHomeAudience): BetaHero {
  const { flags, now } = input;
  if (audience === 'new') {
    return buildNewHero(input);
  }

  const tiles: BetaHeroTile[] = [];
  const { tas, slr, pslf, syf, wellness } = readings;

  /**
   * GAP (employer-investment): the design's "Total Employer Investment" is a
   * roll-up with no single backend field. Read as loan-repayment contributions
   * plus tuition assistance actually paid — the deck's own definition — and
   * only from benefits the partner offers.
   */
  const tuitionLifetime = flags.showTuitionAssistance ? input.model.tasData.summary.totalPaid : 0;
  const tuitionYtd = flags.showTuitionAssistance ? input.tuitionYearToDate : 0;
  const contributionLifetime = flags.showStudentLoanRepayment ? slr.lifetime : 0;
  const contributionYtd = flags.showStudentLoanRepayment ? slr.yearToDate : 0;
  const investmentLifetime = contributionLifetime + tuitionLifetime;
  const investmentYtd = tuitionYtd === null ? null : contributionYtd + tuitionYtd;
  if (investmentLifetime > 0) {
    tiles.push({
      id: 'employer-investment',
      kind: 'metric',
      label: 'Total employer investment',
      value: money(investmentLifetime),
      context:
        investmentYtd === null
          ? 'tuition and loan repayment · year to date unavailable'
          : `${money(investmentYtd)} this year · tuition and loan repayment`,
      source: slr.source === 'live' && tas.source === 'live' ? 'live' : 'demo',
    });
  }

  const candidates: { tile: BetaHeroTile; priority: number; order: number }[] = [];

  if (flags.showPslf && pslf.inUse) {
    candidates.push({
      order: 0,
      priority: soonestDeadline(pslf.rows, now),
      tile: {
        id: 'pslf-payments',
        kind: 'progress',
        label: 'Qualifying payments',
        value: `${pslf.payments} of ${PSLF_REQUIRED_PAYMENTS}`,
        progressValue: pslf.payments,
        progressMax: PSLF_REQUIRED_PAYMENTS,
        context: pslf.asOf
          ? `as of ${formatDate(pslf.asOf, now)} · from your NSLDS file`
          : 'upload your NSLDS file to track them',
        source: pslf.source,
      },
    });
  }

  if (flags.showStudentLoanRepayment && slr.inUse) {
    const next = slr.activity?.nextPayment ?? null;
    const last = slr.activity?.lastPayment ?? null;
    const nextLine = next
      ? `next contribution ${formatMonth(next.paidDate ?? next.benefitCycle, now)} · ${money(next.amount)} · estimate`
      : last
        ? `last contribution ${formatDate(last.paidDate ?? last.benefitCycle, now)}`
        : undefined;
    const tile: BetaHeroTile =
      slr.yearToDate > 0
        ? {
            id: 'slr-contributed',
            kind: 'progress',
            label: 'Contributed this year',
            value: money(slr.yearToDate),
            progressValue: slr.yearToDate,
            progressMax: Math.max(slr.annualLimit, slr.yearToDate),
            context: nextLine ?? `of ${money(slr.annualLimit)} this year`,
            source: slr.source,
          }
        : {
            id: 'slr-lifetime',
            kind: 'metric',
            label: 'Lifetime loan repayment',
            value: money(slr.lifetime),
            context: nextLine,
            source: slr.source,
          };
    candidates.push({ order: 1, priority: soonestDeadline(slr.rows, now), tile });
  }

  if (flags.showTuitionAssistance && tas.inUse) {
    candidates.push({
      order: 2,
      priority: soonestDeadline(tas.rows, now),
      tile:
        tas.used === null
          ? {
              id: 'tas-approved',
              kind: 'metric',
              label: 'Tuition assistance approved this year',
              value: '—',
              context: 'year to date unavailable right now',
              source: tas.source,
            }
          : {
              id: 'tas-approved',
              kind: 'progress',
              label: 'Tuition assistance approved this year',
              value: money(tas.used),
              progressValue: tas.used,
              progressMax: Math.max(tas.limit, tas.used),
              context: tasLimitContext(tas.used, tas.limit),
              source: tas.source,
            },
    });
  }

  if (flags.showSyf && syf.signals?.enrollment === 'enrolled') {
    const deadline = syf.signals.certificationDeadline;
    candidates.push({
      order: 3,
      priority: soonestDeadline(syf.rows, now),
      tile: {
        id: 'syf-deadline',
        kind: 'metric',
        label: '401(k) match certification',
        value: deadline ? formatDate(deadline, now) : 'Enrolled',
        context: deadline
          ? `final date to certify ${syf.signals.benefitYear ?? 'this plan year'} payments`
          : 'qualifying loan payments count toward your match',
        source: 'live',
      },
    });
  }

  if (flags.showFinancialWellness && wellness.inUse && wellness.signals) {
    const { score, asOf } = wellness.signals;
    candidates.push({
      order: 4,
      priority: soonestDeadline(wellness.rows, now),
      tile: {
        id: 'wellness-score',
        kind: 'info',
        label: 'Loan health check',
        body: score ? WELLNESS_SCORE_LABEL[score] : 'Not checked yet',
        context: asOf ? `as of ${formatDate(asOf, now)}` : undefined,
        linkLabel: 'See your insights',
        linkRoute: WELLNESS_ROUTE,
        source: 'live',
      },
    });
  }

  // Recency and urgency pick the tiles: the benefit with the soonest dated row
  // first, then the canonical order, up to the cap.
  candidates.sort((a, b) => a.priority - b.priority || a.order - b.order);
  for (const candidate of candidates) {
    if (tiles.length >= HERO_TILE_CAP) break;
    tiles.push(candidate.tile);
  }

  return {
    title: 'Your Education Benefits',
    tiles,
  };
}

/** The benefit a first-run page leads with, in the order the employer's offering is most likely to matter. */
type LeadBenefit = 'pslf' | 'student-loan-repayment' | 'tuition-assistance' | 'syf' | 'wellness';

function leadBenefit(flags: BetaHomeFlags): LeadBenefit | null {
  if (flags.showPslf) return 'pslf';
  if (flags.showStudentLoanRepayment) return 'student-loan-repayment';
  if (flags.showTuitionAssistance) return 'tuition-assistance';
  if (flags.showSyf) return 'syf';
  if (flags.showFinancialWellness) return 'wellness';
  return null;
}

interface LeadCopy {
  label: string;
  body: string;
  linkLabel: string;
  route: string;
  metric?: { label: string; value: string; context: string };
  how: { title: string; steps: BetaHowStep[] };
  banner: { prompt: string; label: string; route: string };
}

function leadCopy(lead: LeadBenefit): LeadCopy {
  switch (lead) {
    case 'pslf':
      return {
        label: 'Loan Forgiveness (PSLF)',
        body: 'Working in public service can put you on a path to loan forgiveness. Your loan data is the first step.',
        linkLabel: 'Check your PSLF progress',
        route: PSLF_ROUTE,
        metric: {
          label: 'Qualifying payments needed',
          value: String(PSLF_REQUIRED_PAYMENTS),
          context: 'to earn forgiveness',
        },
        how: {
          title: 'How Loan Forgiveness Works',
          steps: [
            {
              id: 'pslf-upload',
              title: 'Upload your loan details',
              description:
                'Your NSLDS file from studentaid.gov shows which payments count toward forgiveness.',
              icon: PhUploadSimple,
            },
            {
              id: 'pslf-estimator',
              title: 'Complete the Forgiveness Estimator',
              description:
                'See whether you could qualify and what forgiveness could look like for you.',
              icon: PhTarget,
            },
            {
              id: 'pslf-form',
              title: 'Submit your PSLF form',
              description: 'Certifying your employment is what makes your payments count.',
              icon: PhFileText,
            },
          ],
        },
        banner: {
          prompt: 'Interested in finding out whether you qualify for loan forgiveness?',
          label: 'Check eligibility',
          route: PSLF_ROUTE,
        },
      };
    case 'student-loan-repayment':
      return {
        label: 'Student Loan Repayment',
        body: 'Your employer contributes to your student loans. Link your loan account and the contributions can start.',
        linkLabel: 'Link your loan account',
        route: LOAN_SUMMARY_ROUTE,
        how: {
          title: 'How Student Loan Repayment Works',
          steps: [
            {
              id: 'slr-link',
              title: 'Link your loan account',
              description: 'Your employer’s contributions are sent to the loan account you choose.',
              icon: PhBank,
            },
            {
              id: 'slr-contribute',
              title: 'Your employer contributes',
              description:
                'Contributions go out on your employer’s schedule, with nothing to submit.',
              icon: PhHandshake,
            },
            {
              id: 'slr-track',
              title: 'Watch your balance shrink',
              description: 'Each contribution and where it landed shows up in your activity.',
              icon: PhReceipt,
            },
          ],
        },
        banner: {
          prompt: 'Interested in having your employer contribute to your student loans?',
          label: 'Link your loans',
          route: SLR_ROUTE,
        },
      };
    case 'tuition-assistance':
      return {
        label: 'Tuition Assistance',
        body: 'Whether you’re mastering a new skill or earning a new credential, Tuition Assistance is here to help cover the costs.',
        linkLabel: 'Explore tuition programs',
        route: TAS_ROUTE,
        how: {
          title: 'How Tuition Assistance Works',
          steps: [
            {
              id: 'tas-explore',
              title: 'Find a program',
              description: 'See which programs you are eligible for and what each one covers.',
              icon: PhGraduationCap,
            },
            {
              id: 'tas-request',
              title: 'Submit your request',
              description: 'Your approver reviews it before you enroll.',
              icon: PhFileText,
            },
            {
              id: 'tas-reimbursed',
              title: 'Get reimbursed',
              description:
                'Send your grades and receipts when the course ends, and payment follows.',
              icon: PhCoins,
            },
          ],
        },
        banner: {
          prompt: 'Interested in your employer helping pay for your education?',
          label: 'Explore programs',
          route: TAS_ROUTE,
        },
      };
    case 'syf':
      return {
        label: 'Secure Your Future',
        body: 'Your student loan payments can earn you a 401(k) match. Enrolling takes a few minutes.',
        linkLabel: 'Enroll in Secure Your Future',
        route: SYF_ENROLLMENT_ROUTE,
        how: {
          title: 'How Secure Your Future Works',
          steps: [
            {
              id: 'syf-enroll',
              title: 'Enroll',
              description: 'Tell us about your plan; it takes about five minutes.',
              icon: PhSignature,
            },
            {
              id: 'syf-pay',
              title: 'Keep paying your loans',
              description: 'Qualifying student loan payments are what earn the match.',
              icon: PhBank,
            },
            {
              id: 'syf-match',
              title: 'Receive your match',
              description: 'Your employer adds it to your 401(k).',
              icon: PhRocketLaunch,
            },
          ],
        },
        banner: {
          prompt: 'Interested in a 401(k) match on the loan payments you already make?',
          label: 'Enroll now',
          route: SYF_ENROLLMENT_ROUTE,
        },
      };
    case 'wellness':
      return {
        label: 'Loan Wellness',
        body: 'See where your loans stand and what to do next, in about five minutes.',
        linkLabel: 'Check your loan health',
        route: WELLNESS_ROUTE,
        how: {
          title: 'How Loan Wellness Works',
          steps: [
            {
              id: 'wellness-upload',
              title: 'Upload a statement',
              description: 'A recent loan statement is all we need.',
              icon: PhUploadSimple,
            },
            {
              id: 'wellness-review',
              title: 'See where you stand',
              description: 'Get a plain summary of your balances, rates and options.',
              icon: PhHeartbeat,
            },
            {
              id: 'wellness-plan',
              title: 'Follow your plan',
              description: 'Take the next step that fits your situation.',
              icon: PhTarget,
            },
          ],
        },
        banner: {
          prompt: 'Interested in a five-minute review of your loans?',
          label: 'Start the check',
          route: WELLNESS_ROUTE,
        },
      };
  }
}

function buildNewHero(input: BetaHomeInput): BetaHero {
  const source: DataSourceType = input.model.section127Info.isLiveData ? 'live' : 'demo';
  const lead = leadBenefit(input.flags);
  const title = 'Let’s get you set up';
  if (!lead) {
    return {
      title,
      lede: 'Welcome. Your benefits will show up here as your employer makes them available.',
      tiles: [],
    };
  }
  const copy = leadCopy(lead);
  const tiles: BetaHeroTile[] = [
    {
      id: 'featured-benefit',
      kind: 'info',
      label: copy.label,
      body: copy.body,
      linkLabel: copy.linkLabel,
      linkRoute: copy.route,
      source,
    },
  ];
  if (copy.metric) {
    tiles.push({
      id: 'featured-metric',
      kind: 'metric',
      label: copy.metric.label,
      value: copy.metric.value,
      context: copy.metric.context,
      source,
    });
  }
  return { title, tiles };
}

function buildHowItWorks(input: BetaHomeInput): BetaHowItWorks | null {
  const lead = leadBenefit(input.flags);
  if (!lead) return null;
  const { how, banner } = leadCopy(lead);
  return { title: how.title, steps: how.steps, banner };
}

const URGENCY_RANK: Record<TaskUrgency, number> = { urgent: 0, soon: 1, onTrack: 2, info: 3 };

/**
 * Hard dates first (soonest first), then what was sent back (most recent
 * first), then unfinished setup (most urgent first), then notices.
 */
export function orderAttentionRows(rows: BetaAttentionRow[]): BetaAttentionRow[] {
  const deadline = sortByDeadline(
    rows.filter((row) => row.group === 'deadline'),
    (row) => row.at
  );
  const returned = rows
    .filter((row) => row.group === 'returned')
    .map((row, index) => ({ row, index, at: parseDate(row.at) ?? -Infinity }))
    .sort((a, b) => b.at - a.at || a.index - b.index)
    .map(({ row }) => row);
  const setup = rows
    .filter((row) => row.group === 'setup')
    .map((row, index) => ({ row, index }))
    .sort((a, b) => URGENCY_RANK[a.row.urgency] - URGENCY_RANK[b.row.urgency] || a.index - b.index)
    .map(({ row }) => row);
  const info = rows.filter((row) => row.group === 'info');
  return [...deadline, ...returned, ...setup, ...info];
}

function buildRecommendations(input: BetaHomeInput): BetaRecommendation[] {
  const phone = phoneRecommendation(input);
  return phone ? [phone] : [];
}

/** `unverified` too: once a number exists the overlay stops, so this is that member's route back. */
function phoneRecommendation(input: BetaHomeInput): BetaRecommendation | null {
  const { phoneCapture, flags } = input;
  if (!flags.showPhoneCapture || !phoneCapture?.eligible) return null;
  if (phoneCapture.state !== 'none' && phoneCapture.state !== 'unverified') return null;
  return {
    id: 'phone-capture',
    icon: PhDeviceMobile,
    title: phoneCapture.title,
    description: phoneCapture.description,
  };
}

/** The next dated thing on the horizon, for the all-clear message. */
function nextUp(input: BetaHomeInput, readings: Readings): string | null {
  const { now } = input;
  const options: { at: number; text: string }[] = [];
  const next = readings.slr.activity?.nextPayment;
  if (input.flags.showStudentLoanRepayment && next) {
    const date = next.paidDate ?? next.benefitCycle;
    const at = parseDate(date);
    if (at !== null) options.push({ at, text: `your ${formatMonth(date, now)} contribution` });
  }
  const deadline = readings.syf.signals?.certificationDeadline;
  if (input.flags.showSyf && readings.syf.signals?.enrollment === 'enrolled' && deadline) {
    const at = parseDate(deadline);
    if (at !== null) {
      options.push({ at, text: `certifying your payments by ${formatDate(deadline, now)}` });
    }
  }
  if (input.flags.showPslf && readings.pslf.lastSubmittedAt) {
    const due = addDays(readings.pslf.lastSubmittedAt, ONE_YEAR_DAYS);
    const at = parseDate(due);
    if (at !== null && at > now) {
      options.push({ at, text: `your PSLF certification by ${formatDate(due, now)}` });
    }
  }
  options.sort((a, b) => a.at - b.at);
  return options[0]?.text ?? null;
}

function buildAttention(input: BetaHomeInput, readings: Readings): BetaAttention {
  const { flags } = input;
  const rows: BetaAttentionRow[] = [
    ...(flags.showTuitionAssistance ? readings.tas.rows : []),
    // Every one of these routes to the SLR dashboard, so a partner without loan
    // repayment would get attention rows pointing at a benefit they do not have.
    ...(flags.showStudentLoanRepayment ? readings.slr.rows : []),
    ...(flags.showPslf ? readings.pslf.rows : []),
    ...(flags.showSyf ? readings.syf.rows : []),
    ...(flags.showFinancialWellness ? readings.wellness.rows : []),
  ];

  const next = nextUp(input, readings);
  return {
    rows: orderAttentionRows(rows),
    emptyTitle: 'You’re all set',
    emptyBody: next
      ? `Nothing needs your attention right now. Next up: ${next}.`
      : 'Nothing needs your attention right now.',
  };
}

const CADENCE_WORD: Record<string, string> = {
  MONTHLY: 'month',
  QUARTERLY: 'quarter',
  SEMI_ANNUALLY: 'half year',
  ANNUALLY: 'year',
  BIWEEKLY: 'two weeks',
  WEEKLY: 'week',
};

const PAGE_STATE_LINE: Partial<Record<SlrPageState, string>> = {
  'waiting-period': 'Waiting period in progress',
  'limit-annual': 'Annual limit reached · contributions resume next benefit year',
  'limit-lifetime': 'Lifetime limit reached',
  'limit-annual-and-lifetime': 'Annual and lifetime limits reached',
  'all-pif': 'All linked loans show paid in full. Upload a statement if that has changed.',
  'no-accounts': 'Add a loan account to receive contributions',
};

function buildBenefitCards(input: BetaHomeInput, readings: Readings): BetaBenefitCard[] {
  const { flags, now } = input;
  const cards: BetaBenefitCard[] = [];
  const { tas, slr, pslf, syf, wellness } = readings;

  if (flags.showTuitionAssistance && tas.inUse) {
    const statusLines: string[] = [];
    if (tas.inReview) statusLines.push(`${plural(tas.inReview, 'request')} in review`);
    if (tas.waitingOnYou) {
      statusLines.push(`${plural(tas.waitingOnYou, 'request')} waiting on you`);
    }
    if (tas.paymentScheduled) {
      statusLines.push(`${plural(tas.paymentScheduled, 'request')} approved, payment scheduled`);
    }
    if (!statusLines.length && tas.paid) {
      statusLines.push(
        `${plural(tas.paid, 'request')} paid · ${money(input.model.tasData.summary.totalPaid)} lifetime`
      );
    }
    if (tas.courseCashReserved > 0 || tas.courseCashUsed > 0) {
      statusLines.push(
        `CourseCash: ${money(tas.courseCashReserved)} reserved on your card · ${money(tas.courseCashUsed)} spent`
      );
    }
    const recent = [...tas.applications]
      .sort(
        (a, b) =>
          (parseDate(b.updatedAt ?? b.createdAt) ?? 0) -
          (parseDate(a.updatedAt ?? a.createdAt) ?? 0)
      )
      .slice(0, 3)
      .map((application) => {
        const touched = application.updatedAt ?? application.createdAt;
        const paid = application.state === TAS_PAID_STATE && application.paidTotal > 0;
        return {
          label: application.programName ?? application.name,
          value: tasStatusLabel(application),
          note: paid
            ? money(application.paidTotal)
            : touched
              ? `since ${formatDate(touched, now)}`
              : undefined,
        };
      });
    const used = tas.used;
    cards.push({
      id: 'tuition-assistance',
      title: 'Tuition Assistance',
      icon: PhGraduationCap,
      route: TAS_ROUTE,
      linkLabel: 'View activity',
      headline: {
        value: used === null ? '—' : money(used),
        label: 'approved this year',
        progress:
          used !== null && tas.limit > 0
            ? { value: used, max: Math.max(tas.limit, used) }
            : undefined,
      },
      statusLines,
      activity: recent,
      source: tas.source,
    });
  }

  if (flags.showStudentLoanRepayment && slr.inUse) {
    const activity = slr.activity;
    const statusLines: string[] = [];
    const cadence = input.plan?.cadence ? CADENCE_WORD[input.plan.cadence] : undefined;
    if (activity?.scheduledMonthlyAmount && cadence) {
      const next = activity.nextPayment
        ? ` · next contribution ${formatMonth(activity.nextPayment.paidDate ?? activity.nextPayment.benefitCycle, now)}`
        : '';
      statusLines.push(`${money(activity.scheduledMonthlyAmount)} per ${cadence}${next}`);
    }
    const stateLine = activity?.pageState ? PAGE_STATE_LINE[activity.pageState] : undefined;
    if (stateLine) statusLines.push(stateLine);
    if (activity?.accountReceivingName) {
      statusLines.push(`Contributions go to ${activity.accountReceivingName}`);
    }

    const list: BetaActivityRow[] = [];
    const last = activity?.returnedPayment ?? activity?.lastPayment ?? null;
    if (last) {
      const date = last.paidDate ?? last.benefitCycle;
      list.push({
        label: 'Last',
        value: `${formatMonth(last.benefitCycle, now)} · ${money(last.amount)}`,
        note:
          last.status === 'failed'
            ? `returned ${formatDate(date, now)}`
            : `sent ${formatDate(date, now)}`,
      });
    }
    if (activity?.nextPayment) {
      const next = activity.nextPayment;
      list.push({
        label: 'This cycle',
        value: `${formatMonth(next.paidDate ?? next.benefitCycle, now)} · ${money(next.amount)}`,
        note: next.status === 'in_progress' ? 'in progress · should be sent soon' : 'preparing',
      });
    }
    if (activity?.scheduledMonthlyAmount && activity.nextPayment && cadence === 'month') {
      const after = parseDate(activity.nextPayment.paidDate ?? activity.nextPayment.benefitCycle);
      if (after !== null) {
        const following = new Date(after);
        following.setMonth(following.getMonth() + 1);
        list.push({
          label: 'Next',
          value: `${formatMonth(following.toISOString(), now)} · ${money(activity.scheduledMonthlyAmount)}`,
          note: 'scheduled',
        });
      }
    }

    cards.push({
      id: 'student-loan-repayment',
      title: 'Loan Repayment',
      icon: PhHandshake,
      route: SLR_ROUTE,
      linkLabel: 'View contributions',
      headline:
        slr.yearToDate > 0
          ? {
              value: money(slr.yearToDate),
              label: 'contributed this year',
              progress: { value: slr.yearToDate, max: Math.max(slr.annualLimit, slr.yearToDate) },
            }
          : { value: money(slr.lifetime), label: 'contributed lifetime' },
      statusLines,
      activity: list,
      source: slr.source,
    });
  }

  if (flags.showPslf && pslf.inUse) {
    const statusLines: string[] = [];
    const form = pslf.latestForm;
    const employer = input.employerName ?? 'your employer';
    if (form?.status === 'created') {
      statusLines.push('Form ready for your signature');
    } else if (form?.status === 'borrower_signed') {
      statusLines.push(
        `Awaiting ${employer}’s signature${form.borrowerCompletedAt ? ` since ${formatDate(form.borrowerCompletedAt, now)}` : ''}`
      );
    } else if (form?.status && PSLF_IN_TRANSIT.has(form.status)) {
      statusLines.push('Being sent to your servicer');
    } else if (form?.status && PSLF_SUBMITTED.has(form.status)) {
      statusLines.push(
        `Submitted${form.submittedAt ? ` ${formatDate(form.submittedAt, now)}` : ''} · sent to your servicer`
      );
    } else if (form?.status === 'rejected') {
      statusLines.push('Returned: your last form was not accepted');
    }
    if (pslf.submittedCount) {
      statusLines.push(
        `${plural(pslf.submittedCount, 'certification')} on file${pslf.lastSubmittedAt ? ` · last submitted ${formatDate(pslf.lastSubmittedAt, now)}` : ''}`
      );
    }
    if (pslf.asOf) {
      statusLines.push(`Payment count from your NSLDS file as of ${formatDate(pslf.asOf, now)}`);
    }
    cards.push({
      id: 'pslf',
      title: 'Loan Forgiveness (PSLF)',
      icon: PhShieldCheck,
      route: PSLF_ROUTE,
      linkLabel: 'View progress',
      headline: {
        value: `${pslf.payments} of ${PSLF_REQUIRED_PAYMENTS}`,
        label: 'qualifying payments',
        progress: { value: pslf.payments, max: PSLF_REQUIRED_PAYMENTS },
      },
      statusLines,
      activity: [],
      footerLink: { label: 'Questions? Talk to a loan coach', route: COACHING_ROUTE },
      source: pslf.source,
    });
  }

  if (flags.showSyf && syf.signals?.enrollment === 'enrolled') {
    const { certificationDeadline, enrolledAt, benefitYear } = syf.signals;
    const statusLines: string[] = [];
    if (enrolledAt) statusLines.push(`Enrolled ${formatDate(enrolledAt, now)}`);
    statusLines.push('Qualifying loan payments count toward your 401(k) match');
    cards.push({
      id: 'syf',
      title: 'Secure Your Future',
      icon: PhRocketLaunch,
      route: SYF_ROUTE,
      linkLabel: 'View matches',
      headline: certificationDeadline
        ? {
            value: formatDate(certificationDeadline, now),
            label: `certification deadline${benefitYear ? ` · ${benefitYear} plan year` : ''}`,
          }
        : { value: 'Enrolled', label: '401(k) match' },
      statusLines,
      activity: [],
      source: 'live',
    });
  }

  if (flags.showFinancialWellness && wellness.inUse && wellness.signals) {
    const { score, asOf, balanceAsOf } = wellness.signals;
    const statusLines: string[] = [];
    if (balanceAsOf) statusLines.push(`Balances as of ${formatDate(balanceAsOf, now)}`);
    if (asOf) statusLines.push(`Checked ${formatDate(asOf, now)}`);
    cards.push({
      id: 'wellness',
      title: 'Loan Wellness',
      icon: PhFirstAidKit,
      route: WELLNESS_ROUTE,
      linkLabel: 'View insights',
      headline: {
        value: score ? WELLNESS_SCORE_LABEL[score] : 'Not checked yet',
        label: 'loan health',
      },
      statusLines,
      activity: [],
      footerLink: {
        label: 'Lower payment or faster payoff? Find your strategy',
        route: STRATEGY_FINDER_ROUTE,
      },
      source: 'live',
    });
  }

  return cards;
}

/**
 * Discover cards: only benefits the employer offers and the person has not
 * started (each reading's own `inUse`, never the page-wide audience), then the
 * tools that help with the loans they have. A new user sees PSLF first when it
 * is offered and the Strategy Finder otherwise, with coaching for everyone with
 * loan benefits.
 */
function buildDiscover(
  input: BetaHomeInput,
  readings: Readings,
  audience: BetaHomeAudience
): BetaDiscoverCard[] {
  const { flags, model } = input;
  const hasLoanBenefit =
    flags.showPslf ||
    flags.showStudentLoanRepayment ||
    flags.showFinancialWellness ||
    flags.showIdr;
  const benefits: BetaDiscoverCard[] = [];
  const tools: BetaDiscoverCard[] = [];

  if (flags.showPslf && !readings.pslf.inUse) {
    benefits.push({
      id: 'pslf',
      title: 'Loan Forgiveness (PSLF)',
      description: 'See if your employer counts toward Public Service Loan Forgiveness.',
      incentive: 'Check eligibility',
      icon: PhShieldCheck,
      route: PSLF_ROUTE,
    });
  }

  if (flags.showStudentLoanRepayment && !readings.slr.inUse) {
    benefits.push({
      id: 'student-loan-repayment',
      title: 'Student Loan Repayment',
      description: 'Your employer contributes directly to your student loan balance.',
      incentive: 'Link your loans',
      icon: PhHandshake,
      route: SLR_ROUTE,
    });
  }

  if (flags.showTuitionAssistance && !readings.tas.inUse) {
    benefits.push({
      id: 'tuition-assistance',
      title: 'Tuition Assistance',
      description: 'Get reimbursed for courses that fit your role.',
      incentive: `up to ${money(model.section127Info.annualLimit)} a year`,
      icon: PhGraduationCap,
      route: TAS_ROUTE,
    });
  }

  if (flags.showSyf && !readings.syf.inUse) {
    benefits.push({
      id: 'syf',
      title: 'Secure Your Future',
      description: 'Earn a retirement match for the loan payments you already make.',
      incentive: 'See how it works',
      icon: PhRocketLaunch,
      route: SYF_ROUTE,
    });
  }

  if (flags.showFinancialWellness && !readings.wellness.inUse) {
    benefits.push({
      id: 'financial-wellness',
      title: 'Loan Wellness Check',
      description: 'A five-minute review of where your loans stand and what to do next.',
      incentive: 'About 5 minutes',
      icon: PhFirstAidKit,
      route: WELLNESS_ROUTE,
    });
  }

  if (hasLoanBenefit) {
    tools.push(
      {
        id: 'strategy-finder',
        title: 'Strategy Finder',
        description: 'Compare repayment plans side by side at your current balances.',
        incentive: 'About 10 minutes',
        icon: PhTarget,
        route: STRATEGY_FINDER_ROUTE,
      },
      {
        id: 'coaching',
        title: 'Loan Coaching',
        description: 'Talk one-on-one with a coach about your repayment options.',
        incentive: 'Schedule a session',
        icon: PhChatsCircle,
        route: COACHING_ROUTE,
      }
    );
  }

  if (flags.showMentoring) {
    tools.push({
      id: 'mentorship',
      title: 'Education Mentoring',
      description: 'Get help planning courses and making the most of your tuition benefit.',
      incentive: 'Make an appointment',
      icon: PhHeadset,
      route: 'authenticated.tas.mentoring',
    });
  }

  if (flags.showLpn) {
    tools.push({
      id: 'lpn',
      title: 'Tuition Discounts',
      description: 'Save on tuition at partner universities.',
      incentive: 'Browse schools',
      icon: PhGlobe,
      route: 'authenticated.lpn.index',
    });
  }

  // A new user without PSLF leads with the Strategy Finder, as the synthesis asks.
  if (audience === 'new' && !benefits.some((card) => card.id === 'pslf')) {
    const finder = tools.findIndex((card) => card.id === 'strategy-finder');
    if (finder >= 0) {
      const [card] = tools.splice(finder, 1);
      return [card!, ...benefits, ...tools].slice(0, DISCOVER_CARD_CAP);
    }
  }

  return [...benefits, ...tools].slice(0, DISCOVER_CARD_CAP);
}

/** Resources, worded as the question they answer, chosen by the person's benefits, ending with support. */
function buildResources(flags: BetaHomeFlags): BetaDiscoverCard[] {
  const hasLoanBenefit =
    flags.showPslf ||
    flags.showStudentLoanRepayment ||
    flags.showFinancialWellness ||
    flags.showIdr;
  const cards: BetaDiscoverCard[] = [
    {
      id: 'knowledge-base',
      title: 'How do my benefits work?',
      description: 'Plain-language guides to every program your employer offers.',
      icon: PhBookOpen,
      route: KNOWLEDGE_BASE_ROUTE,
    },
  ];

  if (flags.showTuitionAssistance) {
    cards.push({
      id: 'tas-how-to',
      title: 'How do I submit a tuition request?',
      description: 'The steps from choosing a program to getting paid.',
      icon: PhReceipt,
      route: TAS_ROUTE,
    });
  }

  if (flags.showIdr) {
    cards.push({
      id: 'idr',
      title: 'Could an income-driven plan lower my payment?',
      description: 'Balance your payments so you only pay what you can afford.',
      icon: PhScales,
      route: 'authenticated.idr.dashboard',
    });
  } else if (hasLoanBenefit) {
    cards.push({
      id: 'repayment-plans',
      title: 'Which repayment plans could I use?',
      description: 'What each federal plan asks of you and what it costs over time.',
      icon: PhCoins,
      route: REPAYMENT_PLANS_ROUTE,
    });
  }

  cards.push(
    {
      id: 'faqs',
      title: 'Have a question?',
      description: 'Answers to the questions people ask most often.',
      icon: PhSealQuestion,
      route: FAQS_ROUTE,
    },
    {
      id: 'support',
      title: 'Need a hand?',
      description: 'Talk to our support team. Most replies arrive within a day.',
      icon: PhLifebuoy,
      route: CONTACT_ROUTE,
    }
  );

  return cards;
}

export function buildBetaHomeModel(input: BetaHomeInput): BetaHomeModel {
  const readings: Readings = {
    tas: readTas(input),
    slr: readSlr(input),
    pslf: readPslf(input),
    syf: readSyf(input),
    wellness: readWellness(input),
  };
  const audience = audienceFor(input.model, input.flags, {
    syfStarted: input.syf?.enrollment !== undefined && input.syf?.enrollment !== 'none',
    hasLinkedLoans: input.wellness?.hasLinkedLoans ?? input.model.pslfData.accounts.length > 0,
  });

  return {
    audience,
    greeting: greetingFor(input.hour),
    firstName: input.firstName,
    hero: buildHero(input, readings, audience),
    attention:
      audience === 'new'
        ? {
            rows: [],
            emptyTitle: 'You’re all set',
            emptyBody: 'Nothing needs your attention right now.',
          }
        : buildAttention(input, readings),
    howItWorks: audience === 'new' ? buildHowItWorks(input) : null,
    recommendations: audience === 'new' ? [] : buildRecommendations(input),
    benefitCards: audience === 'new' ? [] : buildBenefitCards(input, readings),
    discover: buildDiscover(input, readings, audience),
    resources: buildResources(input.flags),
  };
}

/** Re-exported so components and tests can assert the PSLF denominator. */
export { PSLF_REQUIRED_PAYMENTS };
